import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import ContactForm from '../components/ContactForm';
import { 
  FaCloud, 
  FaServer, 
  FaShieldAlt, 
  FaNetworkWired, 
  FaSyncAlt, 
  FaChartLine,
  FaRobot,
  FaDatabase,
  FaGlobe
} from 'react-icons/fa';
import bgImage from '../assets/cloud.jpg';

const CloudServicesPage = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const cloudServices = [
    {
      icon: <FaCloud />,
      title: 'Cloud Infrastructure',
      description: 'Deploy scalable, secure cloud infrastructure with AWS, Azure, or GCP. Optimized for performance and cost-efficiency with auto-scaling capabilities.',
      color: 'linear-gradient(135deg, #3b82f6, #9333ea)'
    },
    {
      icon: <FaServer />,
      title: 'Serverless Solutions',
      description: 'Build event-driven applications using serverless architectures (Lambda, Cloud Functions) to reduce operational overhead and scale automatically.',
      color: 'linear-gradient(135deg, #10b981, #0d9488)'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Cloud Security',
      description: 'Enterprise-grade security with IAM policies, encryption, DDoS protection, and compliance frameworks (SOC2, HIPAA, GDPR).',
      color: 'linear-gradient(135deg,rgb(200, 151, 237),rgb(56, 112, 232))'
    },
    {
      icon: <FaNetworkWired />,
      title: 'Hybrid & Multi-Cloud',
      description: 'Seamlessly integrate on-premises systems with cloud environments and manage workloads across multiple cloud providers.',
      color: 'linear-gradient(135deg,rgb(11, 245, 237),rgb(55, 6, 217))'
    },
    {
      icon: <FaSyncAlt />,
      title: 'DevOps & CI/CD',
      description: 'Automated deployment pipelines with infrastructure-as-code (Terraform, CloudFormation) and container orchestration (Kubernetes, ECS).',
      color: 'linear-gradient(135deg, #6366f1, #3b82f6)'
    },
    {
      icon: <FaChartLine />,
      title: 'Cloud Analytics',
      description: 'Real-time data processing and business intelligence with BigQuery, Redshift, or Snowflake for data-driven decision making.',
      color: 'linear-gradient(135deg, #8b5cf6, #d946ef)'
    },
    {
      icon: <FaRobot />,
      title: 'AI Cloud Services',
      description: 'Deploy and scale AI models with managed ML services, vector databases, and GPU-accelerated cloud instances.',
      color: 'linear-gradient(135deg, #14b8a6, #047857)'
    },
    {
      icon: <FaGlobe />,
      title: 'Global Edge Network',
      description: 'Low-latency content delivery with worldwide CDN and edge computing capabilities for your distributed applications.',
      color: 'linear-gradient(135deg, #06b6d4, #0ea5e9)'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  return (
    <div 
      className="cloud-services-container"
      style={{
        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.92), rgba(15, 23, 42, 0.95)), url(${bgImage})`
      }}
      ref={ref}
    >
      <motion.div
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={containerVariants}
        className="cloud-services-content"
      >
        <motion.h1 
          className="cloud-services-title"
          variants={itemVariants}
        >
          Next-Gen Cloud Solutions
        </motion.h1>

        <motion.p 
          className="cloud-services-description"
          variants={itemVariants}
        >
          Transform your business with our cutting-edge cloud services. From migration to AI integration, we deliver secure, 
          <span className="highlight"> scalable solutions tailored to your digital transformation needs</span>.
        </motion.p>

        <motion.div 
          className="cloud-services-grid"
          variants={containerVariants}
        >
          {cloudServices.map((service, index) => (
            <motion.div
              key={index}
              className="cloud-service-card"
              style={{ background: service.color }}
              variants={itemVariants}
              whileHover={{ 
                y: -5,
                scale: 1.02,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.3)"
              }}
            >
              <div className="service-icon">
                {service.icon}
              </div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-description">{service.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* NovaCrest Enhanced Section */}
        <motion.div 
          className="novacrest-section"
          variants={itemVariants}
          whileHover={{ scale: 1.01 }}
        >
          <h2 className="novacrest-title">
            About NovaCrest Cloud
          </h2>
          <p className="novacrest-description">
            Novacrest is a premier cloud transformation partner helping enterprises harness the full potential of modern cloud architectures.
          </p>
          
          <ul className="novacrest-features">
            {[
              "Multi-cloud certified experts (AWS, Azure, GCP, Oracle)",
              "End-to-end cloud migration and optimization",
              "AI-powered cloud cost management & automation",
              "Global infrastructure with 99.99% SLA uptime",
              "SOC2 Type II, HIPAA, and GDPR compliant solutions",
              "Real-time monitoring and predictive scaling",
              "Kubernetes and serverless architecture specialists",
              "FinOps certified cloud financial management"
            ].map((item, index) => (
              <motion.li
                key={index}
                className="novacrest-feature-item"
                initial={{ opacity: 0, x: -20 }}
                animate={{ 
                  opacity: 1, 
                  x: 0,
                  transition: { delay: 0.5 + index * 0.1 }
                }}
                whileHover={{ x: 5 }}
              >
                <span className="feature-bullet">•</span>
                <span className="feature-text">{item}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
        <ContactForm />
      </motion.div>

      <style jsx>{`
        .cloud-services-container {
          min-height: 100vh;
          padding: 80px 20px;
          color: #fff;
          font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
          background-attachment: fixed;
          background-size: cover;
          background-position: center;
          text-align: center;
        }

        .cloud-services-content {
          max-width: 1280px;
          margin: 0 auto;
        }

        .cloud-services-title {
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 24px;
          background: linear-gradient(to right, #60a5fa, #a855f7);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          text-shadow: none;
        }

        .cloud-services-description {
          font-size: 1.2rem;
          max-width: 800px;
          margin: 0 auto 50px;
          color: #bfdbfe;
          line-height: 1.7;
        }

        .highlight {
          font-weight: 600;
          color: #fff;
        }

        .cloud-services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 24px;
          max-width: 1280px;
          margin: 0 auto;
        }

        .cloud-service-card {
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
          color: #fff;
          text-align: left;
          transition: all 0.3s ease;
          backdrop-filter: blur(12px);
          background-color: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .service-icon {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          margin-bottom: 20px;
          background-color: rgba(255, 255, 255, 0.1);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
        }

        .service-title {
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 12px;
        }

        .service-description {
          font-size: 1rem;
          color: #e2e8f0;
          line-height: 1.6;
        }

        .novacrest-section {
          margin-top: 80px;
          background: linear-gradient(to right, #1e293b, #0f172a);
          border-radius: 24px;
          padding: 40px;
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3);
          max-width: 1280px;
          margin-left: auto;
          margin-right: auto;
        }

        .novacrest-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 24px;
          background: linear-gradient(to right, #60a5fa, #22d3ee);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .novacrest-description {
          font-size: 1.2rem;
          margin-bottom: 40px;
          color: #bfdbfe;
          max-width: 900px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.7;
        }

        .novacrest-features {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
          max-width: 1000px;
          margin: 0 auto;
          padding: 0;
          list-style: none;
        }

        .novacrest-feature-item {
          display: flex;
          align-items: flex-start;
          padding: 16px;
          background-color: rgba(30, 41, 59, 0.5);
          border-radius: 12px;
          border-left: 4px solid #3b82f6;
          transition: all 0.3s ease;
        }

        .feature-bullet {
          display: inline-block;
          margin-right: 12px;
          color: #60a5fa;
          font-size: 1.5rem;
        }

        .feature-text {
          color: #e2e8f0;
          line-height: 1.6;
        }

        @media (max-width: 768px) {
          .cloud-services-title {
            font-size: 2.2rem;
          }
          
          .cloud-services-grid {
            grid-template-columns: 1fr;
          }
          
          .novacrest-features {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    
    </div>
  );
};

export default CloudServicesPage;